import { useTranslation } from 'react-i18next';
import { Icon } from '../primitives';
import MediaLoadingSpinner from '../primitives/MediaLoadingSpinner';
import useConsentGuard from '../../hooks/useConsentGuard';

const OPTION_CARD_CLASS = [
  'relative flex h-[156px] w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl',
  'border border-white/[0.06] bg-white/[0.08] text-text-primary',
  'transition-all duration-200 ease-out hover:bg-white/10',
].join(' ');

function ReferenceOptionCard({
  label,
  iconName,
  preview,
  uploading = false,
  disabled = false,
  testId,
  uploadingTestId,
  onClick,
  children,
}) {
  const content = (
    <>
      <span className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#FFAC0B]/20 text-[#FFC24C]">
        {uploading ? (
          <span className="absolute inset-0" data-testid={uploadingTestId}>
            <MediaLoadingSpinner className="rounded-full bg-black/45" />
          </span>
        ) : preview ? (
          <img src={preview} alt="" className="h-full w-full object-cover" />
        ) : (
          <Icon name={iconName} size={30} className="text-[#FFAC0B]" />
        )}
      </span>
      <span className="font-sans text-base font-semibold leading-none">
        {label}
      </span>
    </>
  );

  if (children) {
    return (
      <label
        className={`${OPTION_CARD_CLASS} cursor-pointer`}
        data-testid={testId}
        aria-disabled={disabled}
      >
        {children}
        <span className="contents" aria-hidden="true">
          {content}
        </span>
      </label>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={OPTION_CARD_CLASS}
      data-testid={testId}
    >
      {content}
    </button>
  );
}

export default function ReferencePickerFields({
  title,
  hint,
  references = [],
  requiredCount = 1,
  uploading = false,
  uploadingSlot = 0,
  busy = false,
  selectedSource = null,
  activeSlot = null,
  onActiveSlotChange,
  onSourceSelect,
  onGalleryFileSelect,
  onRemove,
  cameraLabel,
  galleryLabel,
  removeLabel,
  testIdPrefix = 'photo',
  galleryInputTestId,
  pairTestId,
  showHeader = true,
}) {
  const { t } = useTranslation();
  const { requireConsent } = useConsentGuard();
  const isPair = requiredCount > 1;
  const singleReference = isPair ? null : references[0];
  const activeSource = singleReference
    ? (singleReference.source || selectedSource || 'gallery')
    : null;

  const selectedSlot = isPair && activeSlot !== null ? activeSlot : 0;
  const activeSlotLabel = isPair && activeSlot !== null
    ? t('photoCatalog.photoSlot', 'Photo {{number}}', { number: activeSlot + 1 })
    : null;

  const handleGalleryFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) await onGalleryFileSelect?.(file, selectedSlot);
  };

  const guardNativeGalleryOpen = (event) => {
    let allowed = false;
    requireConsent(() => {
      allowed = true;
    }, 'upload');
    if (!allowed || busy) event.preventDefault();
  };

  const renderSource = (source, iconName, label) => {
    const showPreview = activeSource === source && singleReference?.preview;
    const showUploading = uploading && uploadingSlot === selectedSlot && selectedSource === source;
    const testId = `${testIdPrefix}-${source}-button`;

    if (source === 'gallery') {
      return (
        <ReferenceOptionCard
          label={label}
          iconName={iconName}
          preview={showPreview}
          uploading={showUploading}
          disabled={busy}
          testId={testId}
          uploadingTestId={`${testIdPrefix}-reference-uploading`}
        >
          <input
            type="file"
            accept="image/*,.heic,.heif"
            className="absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0"
            onClick={guardNativeGalleryOpen}
            onChange={handleGalleryFileChange}
            disabled={busy}
            aria-label={label}
            data-testid={galleryInputTestId || `${testIdPrefix}-gallery-direct-input`}
          />
        </ReferenceOptionCard>
      );
    }

    return (
      <ReferenceOptionCard
        label={label}
        iconName={iconName}
        preview={showPreview}
        uploading={showUploading}
        disabled={busy}
        testId={testId}
        uploadingTestId={`${testIdPrefix}-reference-uploading`}
        onClick={() => requireConsent(
          () => onSourceSelect?.(source, selectedSlot),
          'upload',
        )}
      />
    );
  };

  return (
    <div className="relative">
      {showHeader && (
        <div className="mb-5 pr-10">
          <h2 className="font-sans text-sheet-title text-text-primary">
            {activeSlotLabel || title}
          </h2>
          <p className="mt-2 font-sans text-sheet-subtitle text-text-secondary">
            {hint}
          </p>
        </div>
      )}

      {showHeader && activeSlotLabel ? (
        <button
          type="button"
          onClick={() => onActiveSlotChange?.(null)}
          disabled={busy}
          aria-label={t('common.back', 'Back')}
          className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.08] text-text-primary focus:outline-none focus-visible:ring-checkbox focus-visible:ring-base-white"
          data-testid={`${testIdPrefix}-source-back`}
        >
          <Icon name="chevron-left" size={20} />
        </button>
      ) : null}

      {isPair ? (
        activeSlot === null ? (
          <div
            className="mb-5 grid grid-cols-2 gap-3"
            data-testid={pairTestId}
          >
            {Array.from({ length: requiredCount }).map((_, index) => {
              const reference = references[index];
              return (
                <div key={index} className="relative">
                  <ReferenceOptionCard
                    label={t('photoCatalog.photoSlot', 'Photo {{number}}', { number: index + 1 })}
                    iconName="plus"
                    preview={reference?.preview}
                    uploading={uploading && uploadingSlot === index}
                    disabled={busy}
                    testId={`${testIdPrefix}-reference-slot-${index}`}
                    uploadingTestId={`${testIdPrefix}-reference-uploading`}
                    onClick={() => requireConsent(
                      () => onActiveSlotChange?.(index),
                      'upload',
                    )}
                  />
                  {reference ? (
                    <button
                      type="button"
                      aria-label={removeLabel}
                      onPointerDown={(event) => event.stopPropagation()}
                      onTouchStart={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemove?.(index);
                      }}
                      disabled={busy}
                      className="absolute right-2 top-2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/55 text-text-primary"
                      data-testid={`${testIdPrefix}-reference-remove-${index}`}
                    >
                      <Icon name="clear" size={16} />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="mb-5 grid grid-cols-2 gap-3"
            data-testid={`${testIdPrefix}-source-picker`}
          >
            {renderSource('camera', 'camera-bold', cameraLabel)}
            {renderSource('gallery', 'gallery-nav', galleryLabel)}
          </div>
        )
      ) : (
        <div className="mb-5 grid grid-cols-2 gap-3">
          {renderSource('camera', 'camera-bold', cameraLabel)}
          {renderSource('gallery', 'gallery-nav', galleryLabel)}
        </div>
      )}
    </div>
  );
}
